import { Box, Typography, Link } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { STEPS } from '../onboardingSteps'
import { useStep } from '../hooks/useStep'
import { useOnboardingV2 } from '../OnboardingContext'
import { COLORS } from '../theme'
import { stepHeadingSx } from '../styles'

const CHECKLIST = [
  'Basic information about your organisation',
  'Any documents about your organisation (annual reports, strategic plans, etc.)',
  "An idea of something specific you'd like to fund",
]

export default function IntroWelcome() {
  const { goToStep } = useOnboardingV2()

  useStep({})

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Typography
        variant="h5"
        sx={{ ...stepHeadingSx, mb: 1.5, textAlign: 'center' }}
      >
        Welcome to Fundsorter!
      </Typography>

      {/* Time estimate callout */}
      <Box
        sx={{
          display: 'inline-flex',
          bgcolor: COLORS.linkBgLight,
          borderRadius: 2,
          px: 2,
          py: 0.75,
          mb: 1,
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: COLORS.link, fontWeight: 500 }}
        >
          This should take about{' '}
          <Box
            component="span"
            sx={{ position: 'relative', display: 'inline-block' }}
          >
            <svg
              viewBox="0 0 30 12"
              style={{
                position: 'absolute',
                top: '-10px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '24px',
                height: '10px',
                overflow: 'visible',
              }}
            >
              <line
                x1="15"
                y1="10"
                x2="15"
                y2="1"
                stroke={COLORS.link}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <line
                x1="9"
                y1="11"
                x2="4"
                y2="4"
                stroke={COLORS.link}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <line
                x1="21"
                y1="11"
                x2="26"
                y2="4"
                stroke={COLORS.link}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            20
          </Box>{' '}
          minutes
        </Typography>
      </Box>

      <Typography
        sx={{
          color: COLORS.secondary,
          fontSize: '0.7rem',
          mb: 2,
          textAlign: 'center',
        }}
      >
        You can leave and come back anytime.
      </Typography>

      {/* Body text */}
      <Box sx={{ width: '100%', maxWidth: 800, textAlign: 'left' }}>
        <Typography
          variant="body2"
          sx={{ color: COLORS.bodyText, lineHeight: 1.7, mb: 2 }}
        >
          The setup wizard will take you through the process step by step. You
          will set up your organisation profile, create a funding need, search
          for funding opportunity matches, and draft your first application. By
          the end, you'll be well on your way to getting your funding sorted!
        </Typography>

        <Typography
          variant="body2"
          sx={{ color: COLORS.bodyText, lineHeight: 1.7, mb: 2 }}
        >
          If you just want to briefly see how Fundsorter works without going
          through the whole set up process, please{' '}
          <Link
            component="button"
            variant="body2"
            onClick={() => goToStep(STEPS.INTRO_VIDEO)}
            sx={{
              color: COLORS.primary,
              textDecorationColor: COLORS.primary,
              cursor: 'pointer',
              fontWeight: 500,
              '&:hover': { color: COLORS.primaryHover },
            }}
          >
            watch this short intro video
          </Link>
          .
        </Typography>
      </Box>

      {/* Checklist */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 480,
          textAlign: 'left',
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: COLORS.primary,
            fontWeight: 600,
            mb: 1,
          }}
        >
          You'll need:
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {CHECKLIST.map((item, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
              }}
            >
              <CheckCircleOutlineIcon
                sx={{
                  fontSize: 20,
                  color: COLORS.primary,
                  mt: '2px',
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body2"
                sx={{ color: COLORS.bodyText, lineHeight: 1.6 }}
              >
                {item}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Ready prompt */}
      <Typography
        variant="body2"
        sx={{ color: COLORS.secondary, mt: 3, textAlign: 'center' }}
      >
        Ready to proceed? Click Continue to get started.
      </Typography>
    </Box>
  )
}
